import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiPhone, FiMapPin, FiSend, FiArrowRight, FiMessageCircle, FiVideo, FiSettings, FiGlobe } from 'react-icons/fi';
import { toast } from 'react-toastify';
import logo from '../../Images/logo.png';
import styles from './ContactPage.module.css';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    workEmail: '',
    company: '',
    phone: '',
    industry: 'Automotive',
    message: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.workEmail || !formData.message) {
      toast.error("Please fill in all required fields!");
      return;
    }
    toast.success("Message sent successfully! Our team will get back to you.");
    setFormData({
      fullName: '',
      workEmail: '',
      company: '',
      phone: '',
      industry: 'Automotive',
      message: ''
    });
  };

  return (
    <div className={styles.container}>
      {/* Ambient glows */}
      <div className={styles.ambientGlows}>
        <div className={styles.glow1}></div>
        <div className={styles.glow2}></div>
        <div className={styles.glow3}></div>
      </div>

      {/* 🟦 1. HEADER / NAVIGATION */}
      <header className={styles.header} style={{ flex: "none" }}>
        <div className={styles.headerInner}>
          <div className={styles.logoArea} onClick={() => navigate('/')}>
            <img src={logo} alt="SaleSense Logo" className={styles.logoImg} />
            <span className={styles.logoText}>Sailsense <span className={styles.logoCyan}>AI</span></span>
          </div>
          <nav className={styles.navLinks}>
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Home</a>
            <a href="/ai-agents" onClick={(e) => { e.preventDefault(); navigate('/ai-agents'); }}>AI Agents</a>
            <a href="/platform" onClick={(e) => { e.preventDefault(); navigate('/platform'); }}>Platform</a>
            <a href="/sales-operations" onClick={(e) => { e.preventDefault(); navigate('/sales-operations'); }}>Sales Operations</a>
            <a href="/solutions" onClick={(e) => { e.preventDefault(); navigate('/solutions'); }}>Solutions</a>
            <a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}>Pricing</a>
            <a href="/contact" className={styles.activeLink} onClick={(e) => { e.preventDefault(); }}>Contact</a>
          </nav>
          <div className={styles.headerActions}>
            <button className={styles.trialBtn} onClick={() => navigate('/login')}>
              login <FiArrowRight />
            </button>
          </div>
        </div>
      </header>

      {/* Grid line overlay */}
      <div className={styles.gridOverlay}></div>

      {/* 🟦 2. CONTACT INTRO */}
      <section className={styles.sectionBlock}>
        <div className={styles.sectionCenter}>
          <div className={styles.badgeCyan}>
            Contact
          </div>
          <h1 className={styles.sectionCenterTitle}>
            Let's build your <span className={styles.cyanTitle}>AI sales team.</span>
          </h1>
          <p className={styles.sectionSubtitle}>
            Tell us about your business. We'll show you how Sailsense AI captures, qualifies and converts leads — automatically.
          </p>
        </div>

        <div className={styles.contactGrid}>
          {/* Left Column Info Blocks */}
          <div className={styles.infoCol}>
            <div className={styles.infoCard}>
              <div className={styles.infoIconBox}>
                <FiMail />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>EMAIL</span>
                <a href="mailto:Sales@provaantech.com" className={styles.infoText}>Sales@provaantech.com</a>
              </div>
            </div>

            <div className={styles.infoCard}>
              <div className={styles.infoIconBox}>
                <FiPhone />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>PHONE</span>
                <a href="tel:+19739757226" className={styles.infoText}>+1 (973) 975-7226</a>
              </div>
            </div>

            <div className={styles.infoCard}>
              <div className={styles.infoIconBox}>
                <FiMapPin />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>INDIA OFFICE (PUNE)</span>
                <span className={styles.infoText} style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                  Office No: 916, Floor 12, Geras Imperium Raise, Hinjawadi Phase 2, Pune, India - 411057
                </span>
              </div>
            </div>

            <div className={styles.infoCard}>
              <div className={styles.infoIconBox}>
                <FiMapPin />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>USA OFFICE</span>
                <span className={styles.infoText} style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                  1945 McGraw Ave, Bronx, NY, USA - 10462
                </span>
              </div>
            </div>

            <div className={styles.infoCard}>
              <div className={styles.infoIconBox}>
                <FiGlobe />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>WEBSITE</span>
                <a href="https://provaantech.com" target="_blank" rel="noopener noreferrer" className={styles.infoText}>provaantech.com</a>
              </div>
            </div>
          </div>

          {/* Right Column Form */}
          <div className={styles.formCard}>
            <form onSubmit={handleSubmit} className={styles.contactForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="fullName">FULL NAME</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    placeholder="Jane Doe"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="workEmail">WORK EMAIL</label>
                  <input
                    type="email"
                    id="workEmail"
                    name="workEmail"
                    placeholder="jane@company.com"
                    value={formData.workEmail}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="company">COMPANY</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    placeholder="Acme Motors"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="phone">PHONE</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="+91 ..."
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className={styles.formGroupFull}>
                <label htmlFor="industry">INDUSTRY</label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                >
                  <option value="Automotive">Automotive Dealerships</option>
                </select>
              </div>

              <div className={styles.formGroupFull}>
                <label htmlFor="message">HOW CAN WE HELP?</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about your sales workflow..."
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>

              <button type="submit" className={styles.submitBtn}>
                Send Message <FiSend className={styles.sendIcon} />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* 🟦 4. FOOTER */}
      <footer className={styles.footerSection}>
        <div className={styles.footerInner}>
          {/* Brand area left */}
          <div className={styles.footerBrandColumn}>
            <div className={styles.footerBrandLogo} onClick={() => navigate('/')}>
              <img src={logo} alt="SaleSense Logo" className={styles.footerLogoImg} />
              <span>Sailsense AI</span>
            </div>
            <p className={styles.footerBrandText}>
              AI Chat, Voice and Video agents that capture leads, automate conversations and close sales — 24/7.
            </p>
            <p className={styles.footerBrandProductOf}>
              A product of <strong>Provaantech</strong>
            </p>
          </div>

          {/* Brand System center */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Brand System</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>
                <FiMessageCircle style={{ marginRight: '0.5rem' }} /> Sailsense Chatbot
              </li>
              <li className={styles.footerLinkItem}>
                <FiPhone style={{ marginRight: '0.5rem' }} /> ROVIA — Voice AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiVideo style={{ marginRight: '0.5rem' }} /> AIMEE — Video AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiSettings style={{ marginRight: '0.5rem' }} /> Sailsense Platform
              </li>
            </ul>
          </div>

          {/* Industries right */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Focus Industry</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>Automotive Dealerships</li>
            </ul>
          </div>
        </div>

        {/* Bottom row copyright */}
        <div className={styles.footerBottomBar}>
          <div>© 2026 Sailsense AI - Provaantech. All rights reserved.</div>
          <div>Built for high-performance sales teams.</div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
